'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useMonitoring } from '@kit/monitoring/hooks';
import { isTransientNetworkError } from '@kit/shared/lib/is-transient-network-error';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { useCurrentUser } from './use-current-user';

interface PresenceState {
  user_id: string;
  online_at: string;
}

// Global state for online users - shared across all hook instances
let globalOnlineUsers = new Set<string>();
const globalListeners = new Set<() => void>();

function notifyListeners() {
  globalListeners.forEach((listener) => listener());
}

/**
 * Hook to manage global presence tracking
 *
 * This hook:
 * - Subscribes to a global `app-presence` Realtime channel
 * - Tracks the current user's presence with channel.track()
 * - Maintains a set of online user IDs via presence sync/join/leave events
 * - Updates last_active_at in the database via RPC
 *
 * Should be initialized once at the app level via PresenceProvider.
 */
export function usePresence() {
  const client = useSupabase();
  const queryClient = useQueryClient();
  const monitoring = useMonitoring();
  const { userId } = useCurrentUser();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [, forceUpdate] = useState({});

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => forceUpdate({});
    globalListeners.add(listener);
    return () => {
      globalListeners.delete(listener);
    };
  }, []);

  // Set up presence channel
  useEffect(() => {
    if (!userId) return;

    // Cancellation guard for the async setup below: if this effect is torn
    // down (unmount or dep change) before setup finishes, we must NOT bind
    // listeners onto a channel that is about to be orphaned.
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Best-effort last_active_at update, guarded on a live session.
    //
    // `userId` comes from use-current-user, whose getUser() result is cached by
    // TanStack Query for up to 30 min (gcTime) and is not refetched on mount.
    // That cached id can outlive — or diverge from — the actual Supabase
    // session (token expiry, sign-out, or an account switch during the
    // setup-flow token churn on /home/artist/profile/setup).
    //
    // Two failure modes this guards against:
    //   1. No live session → the RPC would run as `anon`, which has no EXECUTE
    //      grant on this SECURITY DEFINER function, failing with
    //      "permission denied for function update_last_active_at" (BALLEE-EF).
    //      Gating on getSession() being non-null prevents that anon call.
    //   2. A live session whose user differs from the cached `userId` (User B
    //      signed in but the TanStack cache still holds User A's id). Passing
    //      the stale `userId` as p_user_id makes it mismatch the RPC's
    //      auth.uid() (the live JWT subject), tripping the guard
    //      "Cannot update another user's last_active_at" (BALLEE-GA).
    //
    // Deriving p_user_id from `session.user.id` (the live session) rather than
    // the cached `userId` fixes both: the RPC always targets whoever is
    // actually authenticated, so p_user_id === auth.uid() by construction and
    // the update lands on the correct row. Genuine authenticated errors still
    // surface.
    const updateLastActiveAt = async () => {
      const {
        data: { session },
      } = await client.auth.getSession();

      if (!session) return null;

      return client.rpc('update_last_active_at', {
        p_user_id: session.user.id,
      });
    };

    const setup = async () => {
      // `client.channel(topic)` REUSES an existing same-topic channel from the
      // client's registry instead of creating a new one. A channel is only
      // pulled out of that registry when its `close` event fires, which happens
      // AFTER `removeChannel()`'s async unsubscribe/leave handshake completes.
      // So a fresh mount of PresenceProvider (e.g. re-entering the user section
      // via client-side navigation) can race a not-yet-closed `app-presence`
      // channel left over from the previous mount. If `client.channel()` hands
      // back that still-subscribed instance, the subsequent
      // `channel.on('presence', …)` calls hit `isJoined()` and throw
      // "cannot add `presence` callbacks for realtime:app-presence after
      // `subscribe()`" (BALLEE-FN). Await full removal of any lingering
      // `app-presence` channel FIRST so `client.channel()` returns a fresh,
      // not-yet-subscribed channel that is safe to bind listeners on. The topic
      // stays shared (`app-presence`) so cross-client presence still works.
      const stale = client
        .getChannels()
        .filter((existing) => existing.topic === 'realtime:app-presence');

      await Promise.all(
        stale.map((existing) => client.removeChannel(existing)),
      );

      if (cancelled) return;

      const channel = client.channel('app-presence', {
        config: {
          presence: {
            key: userId,
          },
        },
      });

      channelRef.current = channel;

      // Handle presence sync - get all current online users
      channel.on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<PresenceState>();
        const onlineIds = new Set<string>();

        Object.values(state).forEach((presences) => {
          presences.forEach((presence) => {
            if (presence.user_id) {
              onlineIds.add(presence.user_id);
            }
          });
        });

        globalOnlineUsers = onlineIds;
        notifyListeners();

        // Invalidate any user status queries
        queryClient.invalidateQueries({ queryKey: ['user-last-active'] });
      });

      // Handle user joining
      channel.on('presence', { event: 'join' }, ({ newPresences }) => {
        newPresences.forEach((presence) => {
          const p = presence as unknown as PresenceState;
          if (p.user_id) {
            globalOnlineUsers.add(p.user_id);
          }
        });
        notifyListeners();
      });

      // Handle user leaving
      channel.on('presence', { event: 'leave' }, ({ leftPresences }) => {
        leftPresences.forEach((presence) => {
          const p = presence as unknown as PresenceState;
          if (p.user_id) {
            globalOnlineUsers.delete(p.user_id);
          }
        });
        notifyListeners();
        // Invalidate to fetch fresh last_active_at
        queryClient.invalidateQueries({ queryKey: ['user-last-active'] });
      });

      // Subscribe and track presence
      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);

          // Track this user's presence
          await channel.track({
            user_id: userId,
            online_at: new Date().toISOString(),
          });

          // Update last_active_at in database
          try {
            const result = await updateLastActiveAt();

            if (result?.error && !isTransientNetworkError(result.error)) {
              // Non-critical - capture genuine RPC errors, but skip transient
              // browser network blips (offline / navigating away), which are
              // expected noise for this best-effort presence update.
              void monitoring.captureException(
                new Error(
                  `Failed to update last_active_at: ${result.error.message}`,
                ),
              );
            }
          } catch {
            // Non-critical - silent fail
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setIsConnected(false);
        }
      });

      // Set up periodic last_active_at update (every 5 minutes while online)
      intervalId = setInterval(
        () => {
          if (channelRef.current && userId) {
            void (async () => {
              try {
                await updateLastActiveAt();
              } catch {
                // Silent fail - non-critical
              }
            })();
          }
        },
        5 * 60 * 1000,
      );
    };

    void setup();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (channelRef.current) {
        // Update last_active_at one final time before leaving
        void (async () => {
          try {
            await updateLastActiveAt();
          } catch {
            // Silent fail - non-critical
          }
        })();
        client.removeChannel(channelRef.current);
        channelRef.current = null;
        setIsConnected(false);
      }
    };
  }, [client, userId, queryClient, monitoring]);

  const getOnlineUsers = useCallback(() => {
    return globalOnlineUsers;
  }, []);

  return {
    isConnected,
    onlineUsers: globalOnlineUsers,
    getOnlineUsers,
  };
}

/**
 * Hook to check if a specific user is online
 *
 * @param userId - The user ID to check
 * @returns true if the user is currently online
 */
export function useIsUserOnline(userId: string | null | undefined): boolean {
  const [, forceUpdate] = useState({});

  // Subscribe to global state changes
  useEffect(() => {
    const listener = () => forceUpdate({});
    globalListeners.add(listener);
    return () => {
      globalListeners.delete(listener);
    };
  }, []);

  if (!userId) return false;
  return globalOnlineUsers.has(userId);
}
